import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

interface QuotePdfPagesProps {
  blob: Blob;
  maxWidth?: number;
}

function QuotePdfPage({ document, pageNumber, maxWidth }: { document: PDFDocumentProxy; pageNumber: number; maxWidth: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask: ReturnType<Awaited<ReturnType<PDFDocumentProxy['getPage']>>['render']> | undefined;
    const render = async () => {
      const page = await document.getPage(pageNumber);
      if (cancelled || !canvasRef.current) return;
      const baseViewport = page.getViewport({ scale: 1 });
      const availableWidth = Math.min(maxWidth, Math.max(280, window.innerWidth - 24));
      const cssScale = availableWidth / baseViewport.width;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: cssScale * pixelRatio });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('เบราว์เซอร์ไม่สามารถแสดงตัวอย่าง PDF ได้');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      canvas.style.width = `${Math.ceil(viewport.width / pixelRatio)}px`;
      canvas.style.height = `${Math.ceil(viewport.height / pixelRatio)}px`;
      renderTask = page.render({ canvas, canvasContext: context, viewport });
      await renderTask.promise;
    };
    void render();
    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [document, maxWidth, pageNumber]);

  return <figure className="mobile-quote-pdf-page">
    <canvas ref={canvasRef} aria-label={`ตัวอย่าง PDF หน้า ${pageNumber}`} />
    <figcaption>หน้า {pageNumber} / {document.numPages}</figcaption>
  </figure>;
}

export function QuotePdfPages({ blob, maxWidth = 900 }: QuotePdfPagesProps) {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let disposed = false;
    let loadingTask: PDFDocumentLoadingTask | undefined;
    setDocument(null);
    setError('');
    const load = async () => {
      const [{ GlobalWorkerOptions, getDocument }, data] = await Promise.all([import('pdfjs-dist'), blob.arrayBuffer()]);
      if (disposed) return;
      GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
      loadingTask = getDocument({ data: new Uint8Array(data) });
      const loadedDocument = await loadingTask.promise;
      if (!disposed) setDocument(loadedDocument);
    };
    void load().catch(() => {
      if (!disposed) setError('แสดงตัวอย่าง PDF ไม่สำเร็จ กรุณาลองใหม่');
    });
    return () => {
      disposed = true;
      void loadingTask?.destroy();
    };
  }, [blob]);

  if (error) return <div className="mobile-pdf-message error" role="alert">{error}</div>;
  if (!document) return <div className="mobile-pdf-message" role="status"><span className="spin" /> กำลังเปิดตัวอย่าง PDF…</div>;

  return <div className="mobile-quote-pdf-pages">
    {Array.from({ length: document.numPages }, (_, index) => <QuotePdfPage document={document} pageNumber={index + 1} maxWidth={maxWidth} key={index + 1} />)}
  </div>;
}
